// Every rule a psy field follows, written once and used in two places: the
// browser checks a field as the person types, and the services check every
// request again, because a request can always be sent without the browser.
// One copy is what keeps the two from ever disagreeing about what is valid.
//
// Each check takes the raw value and returns the sentence to show, or null
// when the value is fine. The sentences are English, written for the person
// rather than for a log; the web app translates them for an Italian device.
//
// No check transforms its value. Trimming and capitalising happen where the
// value is stored or sent, and each check judges the value as it will be once
// they have.

// An email address is one plain mailbox, local@domain, and nothing a mail
// library could read as more: no display name, angle brackets, comments,
// quotes, lists or control characters. The local part is dot-separated atoms
// (RFC 5322 atext); the domain is dot-separated hostname labels with at least
// one dot. Without this, "<a@example.com>" and "a@example.com" were two
// addresses to the resend wait and one mailbox to the mail server (#23).
const mailbox = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

export function checkEmail(value) {
  const email = value.trim();

  if (email === '') {
    return 'Enter your email address';
  }

  // 254 characters in all and 64 before the @ are the limits mail servers keep.
  if (email.length > 254 || email.indexOf('@') > 64 || mailbox.test(email) === false) {
    return 'Enter a valid email address';
  }

  return null;
}

// The one form of an address psy stores, compares, limits and mails to:
// trimmed and lower-cased. Nothing provider-specific — dots and +tags stay —
// so two addresses are the same account only when they are the same mailbox
// to every provider. The browser and both auth endpoints all use it.
export function canonicalEmail(value) {
  return value.trim().toLowerCase();
}

// The name a clinician gives an invited address for their own list. It may
// be left out; when given, it follows the name length rule.
export function checkListName(value) {
  if (value.trim().length > 100) {
    return 'A name can be at most 100 characters';
  }

  return null;
}

// What a clinician writes for the patient when assigning a questionnaire. It
// may be left out; when given, it is a few lines at most.
export function checkInstructions(value) {
  if (value.trim().length > 500) {
    return 'Instructions can be at most 500 characters';
  }

  return null;
}

// A clinician's note on an answer or on a whole questionnaire. It may be
// emptied; it is at most a long paragraph.
export function checkReviewNote(value) {
  if (value.length > 2000) {
    return 'A note can be at most 2000 characters';
  }

  return null;
}

// The code arrives with spaces or dashes when it is pasted from an email, so
// only its digits count.
export function checkCode(value) {
  if (/^\d{6}$/.test(value.replace(/\D/g, '')) === false) {
    return 'Enter the 6-digit code from the email';
  }

  return null;
}

// First and last name share the length rule but each names itself when it is
// missing, so the message says which field is empty.
export function checkFirstName(value) {
  if (value.trim() === '') {
    return 'Enter your first name';
  }

  if (value.trim().length > 100) {
    return 'A name can be at most 100 characters';
  }

  return null;
}

export function checkLastName(value) {
  if (value.trim() === '') {
    return 'Enter your last name';
  }

  if (value.trim().length > 100) {
    return 'A name can be at most 100 characters';
  }

  return null;
}

// Whether a value is a record's id: a UUID in its usual written form. An id
// grants nothing; this only keeps text that could never be one away from the
// database, which would otherwise answer with an error quoting it.
export function isId(value) {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

// Whether a value is a real calendar day written YYYY-MM-DD: 2026-02-30 is
// not, though it has the shape.
export function isCalendarDay(value) {
  if (typeof value !== 'string' || /^\d{4}-\d{2}-\d{2}$/.test(value) === false) {
    return false;
  }

  const day = new Date(`${ value }T00:00:00Z`);

  return Number.isNaN(day.getTime()) === false && day.toISOString().startsWith(value) === true;
}

// A real calendar day between 1900 and today. today is passed in, as a
// YYYY-MM-DD string, because "today" depends on where it is asked: the
// browser passes the device's own day, the service its own. Days compare as
// strings because the format sorts the same way the calendar does.
export function checkBirthDate({ value, today }) {
  if (value === '') {
    return 'Enter your birth date';
  }

  if (isCalendarDay(value) === false) {
    return 'Enter a valid birth date';
  }

  if (value > today) {
    return 'The birth date cannot be in the future';
  }

  if (value < '1900-01-01') {
    return 'Enter a birth date after 1900';
  }

  return null;
}

export function checkGender(value) {
  if ([ 'WOMAN', 'MAN', 'NON_BINARY', 'SELF_DESCRIBED', 'PREFER_NOT_TO_SAY' ].includes(value) === false) {
    return 'Choose one option';
  }

  return null;
}

// Device follows the device's light or dark setting; Light and Dark fix it.
export function checkAppearance(value) {
  if ([ 'DEVICE', 'LIGHT', 'DARK' ].includes(value) === false) {
    return 'Choose one option';
  }

  return null;
}

// Optional, and a format check only: 16 letters and digits, or 11 digits for
// a temporary code. It never says whose code it is.
export function checkFiscalCode(value) {
  const code = value.trim().toUpperCase();

  if (code === '') {
    return null;
  }

  if (/^([A-Z0-9]{16}|\d{11})$/.test(code) === false) {
    return 'A fiscal code has 16 letters and digits, or 11 digits if temporary';
  }

  return null;
}

// A day being recorded: a real calendar day, not after today. today is passed
// in for the same reason as for the birth date.
export function checkRecordDate({ value, today }) {
  if (isCalendarDay(value) === false) {
    return 'Choose a valid day';
  }

  if (value > today) {
    return 'You cannot record a day that has not happened yet';
  }

  return null;
}

// A calendar month, YYYY-MM, that has started: from January of the year 1,
// since the calendar the days use has no year 0, to the month of today,
// YYYY-MM-DD as checkRecordDate takes it. A report covers one such month
// (#96); a month still to come has nothing to report.
export function checkMonth({ value, today }) {
  if (typeof value !== 'string' || /^\d{4}-(0[1-9]|1[0-2])$/.test(value) === false || value.startsWith('0000-') === true) {
    return 'Choose a valid month';
  }

  if (value > today.slice(0, 7)) {
    return 'Choose a valid month';
  }

  return null;
}

// A decimal typed by a person, as the number it stands for: a comma is
// accepted as the decimal mark, as an Italian keyboard types it. Anything
// that is not plain digits with at most one decimal is NaN, which every
// number rule below refuses. Parsing is kept apart from the rules so the
// browser parses what was typed and the services check what arrived as a
// number with the very same rule.
export function readDecimal(value) {
  const decimal = value.trim().replace(',', '.');

  if (/^\d+(\.\d)?$/.test(decimal) === false) {
    return NaN;
  }

  return Number(decimal);
}

// Whether a number has at most one decimal. Compared with a tolerance because
// floating point stores 1.1 as 1.1000000000000001, which times ten is not a
// whole number.
function inTenths(value) {
  return Math.abs(Math.round(value * 10) - value * 10) < 1e-9;
}

// Hours slept as a number: from 0 to 24, at most one decimal. graphql checks
// what it receives with this; the browser reaches it through checkSleepHours.
export function checkHoursSlept(hours) {
  if (Number.isFinite(hours) === false || hours < 0 || hours > 24 || inTenths(hours) === false) {
    return 'Enter the hours slept, from 0 to 24';
  }

  return null;
}

// Kilograms as a number: above 0 and at most 500, at most one decimal. Wide
// on purpose, to catch a typo without judging a weight.
export function checkWeightKg(weight) {
  if (Number.isFinite(weight) === false || weight <= 0 || weight > 500 || inTenths(weight) === false) {
    return 'Enter the weight in kg, for example 70.5';
  }

  return null;
}

// The two fields as typed: optional, so an empty field is fine, and anything
// else must read as a decimal that the number rule accepts.
export function checkSleepHours(value) {
  if (value.trim() === '') {
    return null;
  }

  return checkHoursSlept(readDecimal(value));
}

export function checkWeight(value) {
  if (value.trim() === '') {
    return null;
  }

  return checkWeightKg(readDecimal(value));
}

export function checkMedicationName(value) {
  if (value.trim() === '') {
    return 'Enter the medication’s name';
  }

  if (value.trim().length > 100) {
    return 'A name can be at most 100 characters';
  }

  return null;
}

// Optional, free text as on the sheet ("name / mg").
export function checkDose(value) {
  if (value.trim().length > 20) {
    return 'A dose can be at most 20 characters';
  }

  return null;
}

// What is written for one medication on one day; optional, kept short.
export function checkMedicationEntry(value) {
  if (value.trim().length > 200) {
    return 'An entry can be at most 200 characters';
  }

  return null;
}
